import React from 'react';
import {ImageSource} from 'react-native-vector-icons/Icon';
import {
  ImageStyle,
  View,
  ViewStyle,
  StyleSheet,
  Image,
  Pressable,
} from 'react-native';
import colors from 'theme/Colors';
import {corner_radius, spacing} from 'theme/Units';
import {Text} from 'ui-kits/Text';
import {NativeStackScreenProps} from '@react-navigation/native-stack';
import {RootNavigatorParams, Routes} from 'navigation/Routes';
import {useSelector} from 'react-redux';
import {selectedProjectID} from 'redux/selectors/projectSelector';

type menuProps = Readonly<{
  path: ImageSource;
  menuName: string;
  desc: string;
  color: string;
  onPress?: () => void;
}>;
function Menu(props: menuProps) {
  const {path, menuName, desc, color, onPress} = props;
  const colorStyle: ViewStyle = {borderColor: color};
  const imageStyle: ImageStyle = {width: 50, height: 50};
  return (
    <Pressable style={styles.menuContainer} onPress={onPress}>
      <Image style={imageStyle} source={path} />
      <Text variant="inter24">{menuName}</Text>
      <View style={[styles.border, colorStyle]}></View>
      <Text variant="sourcesanspro16">{desc}</Text>
    </Pressable>
  );
}

export function ProjectManagement({
  navigation,
}: NativeStackScreenProps<RootNavigatorParams>) {
  const selectedProjectId = useSelector(selectedProjectID);
  return (
    <View style={styles.container}>
      <View style={styles.menuWrapper}>
        <Menu
          path={require('assets/illustrations/sprint-manager.png')}
          menuName="Sprint Manager"
          desc="Plan, track, and manage sprints to ensure smooth agile development."
          color="#B961FF"
          onPress={() =>
            navigation.navigate(Routes.PROJECT_SPRINT, {
              id: selectedProjectId?.toString()!,
            })
          }
        />
        <Menu
          path={require('assets/illustrations/task-manager.png')}
          menuName="Task Manager"
          desc="Organize, assign, and monitor tasks to enhance team productivity."
          color="#ABFF61"
          onPress={() =>
            navigation.navigate(Routes.PROJECT_TASK, {
              id: selectedProjectId?.toString()!,
            })
          }
        />
        <Menu
          path={require('assets/illustrations/issue-manager.png')}
          menuName="Issue Manager"
          desc="Identify, track, and resolve project issues efficiently."
          color="#FFDF61"
          onPress={() =>
            navigation.navigate(Routes.PROJECT_ISSUES, {
              id: selectedProjectId?.toString()!,
            })
          }
        />
        <Menu
          path={require('assets/illustrations/backlogs-manager.png')}
          menuName="Backlogs Manager"
          desc="Prioritize and refine upcoming work items for future development cycles."
          color="#61B3FF"
          onPress={() =>
            navigation.navigate(Routes.PROJECT_BACKLOGS, {
              id: selectedProjectId?.toString()!,
            })
          }
        />
        <Menu
          path={require('assets/illustrations/epics-manager.png')}
          menuName="EPICs Manager"
          desc="Manage large project goals by breaking them into smaller, actionable tasks."
          color="#FF9361"
          onPress={() =>
            navigation.navigate(Routes.PROJECT_EPIC, {
              id: selectedProjectId?.toString()!,
            })
          }
        />
        <Menu
          path={require('assets/illustrations/report-manager.png')}
          menuName="Report Manager"
          desc="Generate insightful reports for project performance and progress."
          color="#FF61EF"
          onPress={() =>
            navigation.navigate(Routes.PROJECT_REPORT, {
              id: selectedProjectId?.toString()!,
            })
          }
        />
      </View>
    </View>
  );
}

type styleTypes = {
  menuContainer: ViewStyle;
  border: ViewStyle;
  menuWrapper: ViewStyle;
  container: ViewStyle;
};

const styles: styleTypes = StyleSheet.create({
  menuContainer: {
    width: 265,
    height: 280,
    borderWidth: 1,
    borderColor: colors.palette.stroke,
    shadowColor: 'rgba(0,0,0,0.15)',
    shadowOffset: {width: 0, height: 1},
    shadowRadius: 6,
    backgroundColor: colors.palette.white,
    borderRadius: corner_radius['rounded-lg'],
    paddingHorizontal: spacing['6'],
    paddingVertical: spacing['8'],
    gap: spacing['6'],
  },
  border: {
    borderWidth: 3,
    width: '30%',
  },
  container: {
    backgroundColor: colors.palette.white,
    flex: 1,
    justifyContent: 'center',
  },
  menuWrapper: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr',
    gap: spacing['6'],
    marginHorizontal: 'auto',
    marginVertical: spacing['6'],
  },
});
